<template>
  <Page :title="$t('dispute.title')" v-if="isLoggedIn">
    <template v-slot:default>
      <DisputeSuccess v-if="submittedSuccessfully"/>
      <div v-else>
        <div class="row">
          <Messages id="feedback" v-if="!touched || submitted || !valid"/>
        </div>
        <ValidationGroup alwaysSubmit @submit="send" ref="form" enctype="multipart/form-data">
          <template v-slot:default="{ touched, submitted, valid }">
            <div>
              <div class="label-group">
                <span class="label-heading">{{ $t('dispute.sections.disputeInfo') }}</span>
              </div>
              <ValidationField ref="dispute" class="mb-2 row" :required="isPrivate" :value="details.dispute" highlight>
                <template v-slot:default="{ valid }">
                  <div class="col-sm-3">
                    <div class="row">
                      <label class="col-sm-3 col-form-label">{{ $t('dispute.labels.dispute') }}</label>
                    </div>
                    <div class="row">
                      <label v-if="isPrivate" class="col-form-label-details">{{
                          $t('dispute.labels.disputeInfo')
                        }}</label>
                      <label v-else class="col-form-label-details">{{ $t('dispute.labels.disputeInfoBusiness') }}</label>
                    </div>
                  </div>
                  <div class="col-sm-9 p-0">
                    <div class="row pl-4 align-items-center" v-if="isLoading">
                      <div class="spinner-small">
                        <i class="icon-spinner"/>
                      </div>
                      {{ $t('dispute.loading') }}
                    </div>
                    <SimpleTypeahead
                        v-else
                        autocomplete="off"
                        :input-class="valid ? (touched ? 'is-valid' : '') : 'dropdown-has-error'"
                        :required="isPrivate"
                        id="dispute"
                        @selectItem="fillForm"
                        :items="paymentDefaults"
                        :showOnFocus="true"
                        :itemProjection="item => formatDisputeItem(item)"
                        :placeholder="$t('dispute.labels.disputePlaceholder')"
                    />
                  </div>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="validationMessages.dispute"/>
                </template>
              </ValidationField>
              <div class="label-group">
                <span class="label-heading">{{ $t('dispute.sections.contactInfo') }}</span>
              </div>
              <ValidationField v-if="isPrivate" ref="fullName" class="mb-2 row" required :value="details.fullName"
                               highlight>
                <label for="fullName" class="col-sm-3 col-form-label">
                  {{ $t('dispute.labels.fullName') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <input type="text" id="fullName" class="col-sm-9 form-control" v-model="details.fullName">
                <span
                    class="invalid-feedback invalid-feedback-dispute col-sm-12"
                    v-text="validationMessages.fullName"/>
              </ValidationField>
              <ValidationField
                  v-else
                  ref="businessName"
                  class="mb-2 row"
                  required
                  :value="details.businessName"
                  highlight>
                <label for="businessName" class="col-sm-3 col-form-label">
                  {{ $t('dispute.labels.businessName') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <input type="text" id="businessName" class="col-sm-9 form-control" v-model="details.businessName">
                <span
                    class="invalid-feedback invalid-feedback-dispute col-sm-12"
                    v-text="validationMessages.businessName"/>
              </ValidationField>
              <ValidationField
                  class="mb-2 row"
                  :pattern="isPrivate ? subjectCodeRegex.source : registryCodeRegex.source"
                  :maxlength="isPrivate ? 11 : 8"
                  type="number"
                  required
                  number
                  ref="subjectCode"
                  :value="details.subjectCode"
                  highlight
              >
                <label for="code" class="col-sm-3 col-form-label">
                  {{ isPrivate ? $t('dispute.labels.subjectCode') : $t('dispute.labels.registryCode') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <input
                    @input="checkFieldLength"
                    :maxlength="isPrivate ? 11 : 8"
                    type="number"
                    class="col-sm-9 form-control"
                    id="code"
                    v-model="details.subjectCode">
                <span
                    class="invalid-feedback invalid-feedback-dispute col-sm-12"
                    v-text="isPrivate ? validationMessages.subjectCode : validationMessages.registryCode"/>
              </ValidationField>
              <ValidationField ref="email" class="mb-2 row" email required :value="details.email" highlight>
                <label for="email" class="col-sm-3 col-form-label">{{ $t('dispute.labels.email') }}<span
                    class="marker step-by-step" :title="$t('form.required')"> * </span></label>
                <input type="email" class="col-sm-9 form-control" id="email" v-model="details.email">
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="validationMessages.email"/>
              </ValidationField>
              <ValidationField ref="creditorName" class="mb-2 row" required :value="details.creditorName" highlight>
                <label for="creditorName" class="col-sm-3 col-form-label">{{ $t('dispute.labels.creditorName') }}<span
                    class="marker step-by-step" :title="$t('form.required')"> * </span></label>
                <input type="text" id="creditorName" class="col-sm-9 form-control" v-model="details.creditorName">
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                      v-text="validationMessages.creditorName"/>
              </ValidationField>
              <div v-if="!isPrivate">
                <hr class="divider">
                <div class="label-group">
                  <span class="label-heading">{{ $t('dispute.sections.groundsForRightOfRepresentation') }}</span>
                </div>
                <ValidationField ref="representativeName" class="mb-2 row" required :value="details.representativeName"
                                 highlight>
                  <label for="representativeName"
                         class="col-sm-3 col-form-label">{{ $t('dispute.labels.representativeName') }}<span
                      class="marker step-by-step" :title="$t('form.required')"> * </span></label>
                  <input type="text" id="representativeName" class="col-sm-9 form-control"
                         v-model="details.representativeName">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.representativeName"/>
                </ValidationField>
                <div class="mb-2 row">
                  <div class="col-sm-6">
                    <ValidationField
                        class="row"
                        highlight
                        type="checkbox"
                        :required="!isLawFilled"
                        :class="isLawFilled ? '' : 'has-error'"
                        :value="details.isRepresentativeByLaw"
                        ref="isRepresentativeByLaw"
                    >
                      <span class="col-sm-6 col-form-label label-replacement">{{ $t('dispute.labels.law') }}<span
                          class="marker step-by-step" :title="$t('form.required')"> * </span></span>
                      <div class="col-sm-6">
                        <input
                            :disabled="!!details.authorisationFile"
                            type="checkbox"
                            class="dispute-checkbox"
                            id="rightOfRepresentationByLaw"
                            v-model="details.isRepresentativeByLaw"
                        >
                        <label for="rightOfRepresentationByLaw"/>
                      </div>
                      <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="validationMessages.law"/>
                    </ValidationField>
                  </div>
                  <div class="col-sm-6">
                    <ValidationField
                        ref="authorisationFileField"
                        class="row"
                        :class="showAuthFileError ? 'has-error' : ''"
                        :required="!details.isRepresentativeByLaw"
                        :value="details.authorisationFile"
                    >
                      <label for="rightOfRepresentation"
                             class="col-sm-3 col-form-label">
                        {{ $t('dispute.labels.authorisation') }}
                        <span v-if="!details.isRepresentativeByLaw" class="marker step-by-step"
                              :title="$t('form.required')"> * </span>
                      </label>
                      <Dropzone
                          name="authorisationFile"
                          ref="authorisationFile"
                          :maxSize="20 * 1024 * 1024"
                          :required="!details.isRepresentativeByLaw"
                          :multiple="false"
                          :disabled="details.isRepresentativeByLaw"
                          @change="uploadFiles($event, 'authorisationFile')"
                          class="col-md-9"
                          accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.bdoc,.cdoc,.asice,.pst,.ost,.msg,.xlsx"
                      >
                      </Dropzone>
                      <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                            v-text="validationMessages.authorisationFile"/>
                    </ValidationField>
                  </div>
                </div>
              </div>

              <hr class="divider">
              <div class="label-group">
                <span class="label-heading">{{ $t('dispute.sections.reasonDetails') }}</span>
              </div>

              <ValidationField ref="reason" class="mb-2 row" required :value="details.reason" highlight>
                <label for="reason" class="col-sm-3 col-form-label">
                  {{ $t('dispute.labels.reason') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <select id="reason" class="col-sm-9 form-control" v-model="details.reason">
                  <option value="" disabled>{{ $t('dispute.labels.reasonPlaceholder') }}</option>
                  <option v-for="option in reasonOptions" :key="option.value" :value="option.value">
                    {{ $t(option.labelKey) }}
                  </option>
                </select>
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="validationMessages.reason"/>
              </ValidationField>

              <template v-if="details.reason === 'paid'">

                <ValidationField required ref="paidDate" class="mb-2 row" :value="details.paidDate" highlight>
                  <label for="paidDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.paidDate') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>

                  <input type="date" id="paidDate" class="col-sm-2 form-control" v-model="details.paidDate">

                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="validationMessages.paidDate"/>
                </ValidationField>

                <ValidationField required ref="paidAmount" class="mb-2 row" :value="details.paidAmount" highlight>
                  <label for="paidAmount" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.paidAmount') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>

                  <input type="number" min="0" step="0.01" id="paidAmount" class="col-sm-9 form-control"
                         v-model="details.paidAmount">

                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.paidAmount"/>
                </ValidationField>

                <ValidationField required ref="paidNotifyDate" class="mb-2 row" :value="details.paidNotifyDate" highlight>
                  <label for="paidNotifyDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.paidNotifyDate') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>

                  <input type="date" id="paidNotifyDate" class="col-sm-2 form-control" v-model="details.paidNotifyDate">

                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.paidNotifyDate"/>
                </ValidationField>

              </template>

              <template v-if="details.reason === 'expired'">
                <ValidationField required ref="expiredDate" class="mb-2 row" :value="details.expiredDate" highlight>
                  <label for="expiredDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.expiredDate') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <input type="date" id="expiredDate" class="col-sm-2 form-control" v-model="details.expiredDate">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.expiredDate"/>
                </ValidationField>
                <ValidationField required ref="expiredReason" class="mb-2 row" :value="details.expiredReason" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.expiredReason') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="expiredReason"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.expiredReason"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.expiredReason"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'wrongData'">
                <ValidationField required ref="wrongDataDetails" class="mb-2 row" :value="details.wrongDataDetails"
                                 highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.wrongDataDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="wrongDataDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.wrongDataDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.wrongDataDetails"/>
                </ValidationField>
                <ValidationField required ref="correctDataDetails" class="mb-2 row" :value="details.correctDataDetails"
                                 highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.correctDataDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="correctDataDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.correctDataDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.correctDataDetails"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'substantiveDispute'">
                <ValidationField required ref="substantiveDisputeNotice" class="mb-2 row"
                                 :value="details.substantiveDisputeNotice" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.substantiveDisputeNotice') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="substantiveDisputeNotice"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.substantiveDisputeNotice"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.substantiveDisputeNotice"/>
                </ValidationField>
                <ValidationField required ref="substantiveDisputeDetails" class="mb-2 row"
                                 :value="details.substantiveDisputeDetails" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.substantiveDisputeDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="substantiveDisputeDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.substantiveDisputeDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.substantiveDisputeDetails"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'notNotified'">
                <ValidationField required ref="notNotifiedDetails" class="mb-2 row" :value="details.notNotifiedDetails"
                                 highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.notNotifiedDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="notNotifiedDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.notNotifiedDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.notNotifiedDetails"/>
                </ValidationField>
                <ValidationField required ref="notNotifiedContactChange" class="mb-2 row"
                                 :value="details.notNotifiedContactChange" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.notNotifiedContactChange') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="notNotifiedContactChange"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.notNotifiedContactChange"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.notNotifiedContactChange"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'bankruptcy'">
                <ValidationField ref="bankruptcyStartDate" class="mb-2 row" :value="details.bankruptcyStartDate"
                                 highlight>
                  <label for="bankruptcyStartDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.bankruptcyStartDate') }}
                  </label>
                  <input type="date" id="bankruptcyStartDate" class="col-sm-2 form-control"
                         v-model="details.bankruptcyStartDate">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.bankruptcyStartDate"/>
                </ValidationField>
                <ValidationField ref="bankruptcyEndDate" class="mb-2 row" :value="details.bankruptcyEndDate" highlight>
                  <label for="bankruptcyEndDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.bankruptcyEndDate') }}
                  </label>
                  <input type="date" id="bankruptcyEndDate" class="col-sm-2 form-control"
                         v-model="details.bankruptcyEndDate">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.bankruptcyEndDate"/>
                </ValidationField>
                <ValidationField required ref="reliefStatusDetails" class="mb-2 row" :value="details.reliefStatusDetails"
                                 highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.reliefStatusDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="reliefStatusDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.reliefStatusDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.reliefStatusDetails"/>
                </ValidationField>
                <ValidationField ref="reliefStartDate" class="mb-2 row" :value="details.reliefStartDate" highlight>
                  <label for="reliefStartDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.reliefStartDate') }}
                  </label>
                  <input type="date" id="reliefStartDate" class="col-sm-2 form-control" v-model="details.reliefStartDate">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.reliefStartDate"/>
                </ValidationField>
                <ValidationField ref="reliefEndDate" class="mb-2 row" :value="details.reliefEndDate" highlight>
                  <label for="reliefEndDate" class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.reliefEndDate') }}
                  </label>
                  <input type="date" id="reliefEndDate" class="col-sm-2 form-control" v-model="details.reliefEndDate">
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.reliefEndDate"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'rightsViolation'">
                <ValidationField required ref="rightsViolationDetails" class="mb-2 row"
                                 :value="details.rightsViolationDetails" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.rightsViolationDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="rightsViolationDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.rightsViolationDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.rightsViolationDetails"/>
                </ValidationField>
                <ValidationField required ref="rightsViolationContacted" class="mb-2 row"
                                 :value="details.rightsViolationContacted" highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.rightsViolationContacted') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="rightsViolationContacted"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.rightsViolationContacted"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.rightsViolationContacted"/>
                </ValidationField>
              </template>

              <template v-if="details.reason === 'other'">
                <ValidationField required ref="otherReasonDetails" class="mb-2 row" :value="details.otherReasonDetails"
                                 highlight>
                  <label class="col-sm-3 col-form-label">
                    {{ $t('dispute.labels.otherReasonDetails') }}
                    <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                  </label>
                  <AutosizeTextarea>
                    <template v-slot:default="{ resize }">
                      <textarea
                          id="otherReasonDetails"
                          class="col-sm-9 form-control textarea"
                          @input="resize"
                          v-model="details.otherReasonDetails"
                      />
                    </template>
                  </AutosizeTextarea>
                  <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                        v-text="validationMessages.otherReasonDetails"/>
                </ValidationField>
              </template>

              <ValidationField
                  v-if="showReasonFiles"
                  ref="reasonFiles"
                  class="mb-2 row"
                  :required="isReasonFileRequired"
                  :value="reasonFilesValue"
                  highlight
                  :class="showAdditionalFileError ? 'has-error' : ''"
              >
                <label for="additionalFiles" class="col-sm-3 col-form-label">
                  {{ $t(reasonFileLabelKey) }}
                  <span v-if="isReasonFileRequired" class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <Dropzone
                    name="additionalFiles"
                    ref="additionalFiles"
                    :maxSize="20 * 1024 * 1024"
                    :required="true"
                    :multiple="true"
                    @change="uploadFiles($event, 'additionalFiles')"
                    class="col-sm-9"
                    accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.bdoc,.cdoc,.asice,.pst,.ost,.msg,.xlsx"
                ></Dropzone>
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                      v-text="validationMessages.additionalFiles"/>
              </ValidationField>
              <div v-if="reasonInfoKey" class="mb-2 row">
                <div class="col-sm-12">
                  <div class="alert alert-info" v-html="$t(reasonInfoKey)"/>
                </div>
              </div>
              <ValidationField ref="desiredOutcome" class="mb-2 row" required :value="details.desiredOutcome" highlight>
                <label for="desiredOutcome" class="col-sm-3 col-form-label">
                  {{ $t('dispute.labels.desiredOutcome') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <select id="desiredOutcome" class="col-sm-9 form-control" v-model="details.desiredOutcome">
                  <option value="" disabled>{{ $t('dispute.labels.desiredOutcomePlaceholder') }}</option>
                  <option v-for="option in desiredOutcomeOptions" :key="option.value" :value="option.value">
                    {{ $t(option.labelKey) }}
                  </option>
                </select>
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                      v-text="validationMessages.desiredOutcome"/>
              </ValidationField>
              <ValidationField
                  v-if="details.desiredOutcome === 'other'"
                  ref="desiredOutcomeOther"
                  class="mb-2 row"
                  required
                  :value="details.desiredOutcomeOther"
                  highlight
              >
                <label class="col-sm-3 col-form-label">
                  {{ $t('dispute.labels.desiredOutcomeOther') }}
                  <span class="marker step-by-step" :title="$t('form.required')"> * </span>
                </label>
                <AutosizeTextarea>
                  <template v-slot:default="{ resize }">
                    <textarea
                        id="desiredOutcomeOther"
                        class="col-sm-9 form-control textarea"
                        @input="resize"
                        v-model="details.desiredOutcomeOther"
                    />
                  </template>
                </AutosizeTextarea>
                <span class="invalid-feedback invalid-feedback-dispute col-sm-12"
                      v-text="validationMessages.desiredOutcomeOther"/>
              </ValidationField>
              <div class="structured-footer">
                <button
                    type="submit"
                    :disabled="!valid || !isLawFilled"
                    class="btn btn-success btn-lg">{{ $t('dispute.submit') }}
                </button>
              </div>
            </div>
          </template>
        </ValidationGroup>
      </div>
    </template>
  </Page>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import axios from 'axios';
import SimpleTypeahead from 'vue3-simple-typeahead';
import ValidationGroup from '../components/ValidationGroup';
import ValidationField from '../components/ValidationField';
import Page from './Page';
import AutosizeTextarea from '../components/AutosizeTextarea';
import Messages from '../components/Messages';
import DisputeSuccess from '../components/DisputeSuccess.vue';
import Checkbox from '../components/Checkbox';
import 'bootstrap/scss/bootstrap.scss';

import 'vue3-simple-typeahead/dist/vue3-simple-typeahead.css';
import Dropzone from '@/components/Dropzone.vue';

const REASON_OPTIONS = [
  {
    value: 'paid',
    labelKey: 'dispute.reasons.paid'
  },
  {
    value: 'expired',
    labelKey: 'dispute.reasons.expired'
  },
  {
    value: 'wrongData',
    labelKey: 'dispute.reasons.wrongData'
  },
  {
    value: 'substantiveDispute',
    labelKey: 'dispute.reasons.substantiveDispute'
  },
  {
    value: 'notNotified',
    labelKey: 'dispute.reasons.notNotified'
  },
  {
    value: 'bankruptcy',
    labelKey: 'dispute.reasons.bankruptcy',
    privateOnly: true
  },
  {
    value: 'rightsViolation',
    labelKey: 'dispute.reasons.rightsViolation',
    privateOnly: true
  },
  {
    value: 'other',
    labelKey: 'dispute.reasons.other'
  },
];

const REASON_INFO_KEYS = {
  paid: 'dispute.info.paid',
  expired: 'dispute.info.expired',
  notNotified: 'dispute.info.notNotified',
  bankruptcy: 'dispute.info.bankruptcy',
  rightsViolation: 'dispute.info.rightsViolation',
};

const REASON_FILE_LABEL_KEYS = {
  paid: 'dispute.labels.reasonFilesPaid',
  expired: 'dispute.labels.reasonFilesExpired',
  wrongData: 'dispute.labels.reasonFilesWrongData',
  substantiveDispute: 'dispute.labels.reasonFilesSubstantiveDispute',
  notNotified: 'dispute.labels.reasonFilesNotNotified',
  bankruptcy: 'dispute.labels.reasonFilesBankruptcy',
  rightsViolation: 'dispute.labels.reasonFilesRightsViolation',
  other: 'dispute.labels.reasonFilesOther',
};

const REQUIRED_REASON_FILES = new Set([
  'paid',
  'wrongData',
  'substantiveDispute',
  'bankruptcy',
]);

const REQUIRED_REASON_FILE_MESSAGE_KEYS = {
  paid: 'dispute.validation.missingPaidFile',
  substantiveDispute: 'dispute.validation.missingDisputeFiles',
  bankruptcy: 'dispute.validation.missingBankruptcyFile',
};

export default {
  components: {
    DisputeSuccess,
    Dropzone,
    ValidationField,
    ValidationGroup,
    Page,
    AutosizeTextarea,
    Messages,
    Checkbox,
    SimpleTypeahead,
  },
  props: {
    isPrivate: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showAdditionalFileError: false,
    showAuthFileError: false,
    selectedDispute: '',
    isLoading: false,
    isLawFilled: true,
    submittedSuccessfully: false,
    details: {
      isPrivate: false,
      dispute: null,
      reason: '',
      fullName: '',
      email: '',
      subjectCode: '',
      businessName: '',
      creditorName: '',
      representativeName: '',
      isRepresentativeByLaw: false,
      authorisationFile: null,
      paidDate: '',
      paidAmount: '',
      paidNotifyDate: '',
      expiredDate: '',
      expiredReason: '',
      wrongDataDetails: '',
      correctDataDetails: '',
      substantiveDisputeNotice: '',
      substantiveDisputeDetails: '',
      notNotifiedDetails: '',
      notNotifiedContactChange: '',
      bankruptcyStartDate: '',
      bankruptcyEndDate: '',
      reliefStatusDetails: '',
      reliefStartDate: '',
      reliefEndDate: '',
      rightsViolationDetails: '',
      rightsViolationContacted: '',
      otherReasonDetails: '',
      desiredOutcome: '',
      desiredOutcomeOther: '',
      additionalFiles: null,
    },
    validationMessages: {},
  }),
  watch: {
    profile: {
      immediate: true,
      handler: 'syncWithProfile',
    },
    'details.isRepresentativeByLaw': function () {
      this.isLawInputFilled();
      if (this.$refs.authorisationFileField) {
        this.$refs.authorisationFileField.updateValidityLater();
      }
    },
    'details.authorisationFile': function () {
      this.isLawInputFilled();
      if (this.$refs.isRepresentativeByLaw) {
        this.$refs.isRepresentativeByLaw.updateValidityLater();
      }
    },
    'details.reason': function () {
      this.clearFeedback();
      this.resetReasonFields();
      if (this.details.reason === 'paid') {
        this.details.paidAmount = this.selectedDispute.finalSize;
      }
    },
    details: {
      deep: true,
      handler() {
        this.clearFeedback();
      }
    },
    'details.desiredOutcome': function (value) {
      if (value !== 'other') {
        this.details.desiredOutcomeOther = '';
      }
    },
    '$i18n.locale': function () {
      this.populateValidationMessages();
    },
  },
  created() {
    if (!this.isLoggedIn) {
      this.$router.push({
        name: `home-${this.$i18n.locale}`,
        params: { lang: this.$i18n.locale }
      });
    }
    this.apiUrl = import.meta.env.VITE_API_URL;
    this.subjectCodeRegex = /^\d{11}$/;
    this.registryCodeRegex = /^\d{8}$/;
    this.paymentDefaultsGroups = [
      'waitingPaymentDefaults',
      'activePaymentDefaults',
      'endedPaymentDefaults',
    ];
    this.dataGroups = {
      private: ['fullName'],
      business: ['businessName', 'representativeName', 'isRepresentativeByLaw', 'authorisationFile'],
      both: [
        'email',
        'isPrivate',
        'dispute',
        'subjectCode',
        'creditorName',
        'reason',
        'paidDate',
        'paidAmount',
        'paidNotifyDate',
        'expiredDate',
        'expiredReason',
        'wrongDataDetails',
        'correctDataDetails',
        'substantiveDisputeNotice',
        'substantiveDisputeDetails',
        'notNotifiedDetails',
        'notNotifiedContactChange',
        'bankruptcyStartDate',
        'bankruptcyEndDate',
        'reliefStatusDetails',
        'reliefStartDate',
        'reliefEndDate',
        'rightsViolationDetails',
        'rightsViolationContacted',
        'otherReasonDetails',
        'desiredOutcome',
        'desiredOutcomeOther',
        'additionalFiles',
      ],
    };
    this.paymentDefaults = [];
    this.details.isPrivate = this.isPrivate;
    this.populateValidationMessages();
    this.loadPaymentDefaults();
  },
  computed: {
    ...mapState('session', ['profile']),
    ...mapGetters('session', ['isLoggedIn']),
    reasonOptions() {
      return REASON_OPTIONS.filter((option) => !option.privateOnly || this.isPrivate);
    },
    reasonInfoKey() {
      return REASON_INFO_KEYS[this.details.reason] || null;
    },
    reasonFileLabelKey() {
      return REASON_FILE_LABEL_KEYS[this.details.reason] || 'dispute.labels.additionalFiles';
    },
    reasonFilesCount() {
      return this.details.additionalFiles ? this.details.additionalFiles.length : 0;
    },
    reasonFilesValue() {
      return this.reasonFilesCount ? '1' : '';
    },
    isReasonFileRequired() {
      return REQUIRED_REASON_FILES.has(this.details.reason);
    },
    showReasonFiles() {
      return !!this.details.reason;
    },
    desiredOutcomeOptions() {
      return [
        {
          value: 'delete',
          labelKey: 'dispute.outcomes.delete'
        },
        {
          value: 'correct',
          labelKey: 'dispute.outcomes.correct'
        },
        {
          value: 'end',
          labelKey: 'dispute.outcomes.end'
        },
        {
          value: 'other',
          labelKey: 'dispute.outcomes.other'
        },
      ];
    },
  },
  methods: {
    ...mapActions('alerts', ['showMessage', 'clearMessages']),
    ...mapActions('product', ['getMyPaymentDefaults']),
    ...mapActions('product', ['getRelatedCompanyPaymentDefaults']),

    formatDisputeItem(item) {
      let str = '';
      if (!this.isPrivate) str += `${item.companyName}, `;
      str += item.creditor ? `${item.creditor}, ` : 'omanik puudub, ';
      str += item.startDate;
      if (item.finalSize) {
        str += `, ${parseFloat(item.finalSize)
            .toFixed(2)} €`;
      }
      return str;
    },
    fillForm(item) {
      this.selectedDispute = item;

      this.details.dispute = item.id;
      this.details.businessName = item.companyName;
      this.details.creditorName = item.creditor;
      this.details.paidAmount = item.finalSize;

      this.$refs.creditorName.updateValidityLater();
      this.$refs.dispute.updateValidityLater();

      if (this.isPrivate) return;

      axios.get(`${this.apiUrl}/company/subjectcode/${item.companyName}`)
          .then((resp) => {
            if (resp.data) {
              this.details.subjectCode = resp.data;
            }
          });
    },
    syncWithProfile() {
      if (!this.profile) return;
      this.details.email = this.profile.email;
      if (!this.isPrivate) {
        this.details.representativeName = this.profile.name;
        return;
      }
      this.details.subjectCode = this.profile.personId.toString();
      this.details.fullName = this.profile.name;
    },
    loadPaymentDefaults() {
      this.isLoading = true;
      if (this.isPrivate) {
        this.getMyPaymentDefaults()
            .then((details) => {
              if (details.isSample && process.env.NODE_ENV === 'production') return;
              let result = [];
              for (const group of this.paymentDefaultsGroups) {
                if (details[group].length) result = result.concat(details[group]);
              }
              this.paymentDefaults = result;
            })
            .finally(() => {
              this.isLoading = false;
            });
      } else {
        this.getRelatedCompanyPaymentDefaults()
            .then((details) => {
              if (details.isSample && process.env.NODE_ENV === 'production') return;
              let result = [];
              for (const group of this.paymentDefaultsGroups) {
                Object.keys(details[group])
                    .forEach((companyName, _) => {
                      const defaults = details[group][companyName];
                      if (defaults.length) {
                        for (const pd of defaults) {
                          pd.companyName = companyName;
                        }
                        result = result.concat(defaults);
                      }
                    });
              }
              this.paymentDefaults = result;
            })
            .finally(() => {
              this.isLoading = false;
            });
      }
    },
    populateValidationMessages() {
      this.validationMessages = {
        fullName: this.$t('dispute.validation.fullName'),
        email: this.$t('dispute.validation.email'),
        subjectCode: this.$t('dispute.validation.subjectCode'),
        businessName: this.$t('dispute.validation.businessName'),
        creditorName: this.$t('dispute.validation.creditorName'),
        isRepresentativeByLaw: this.$t('dispute.validation.law'),
        additionalFiles: this.$t('dispute.validation.additionalFiles'),
        authorisationFile: this.$t('dispute.validation.authorisation'),
        registryCode: this.$t('dispute.validation.registryCode'),
        representativeName: this.$t('dispute.validation.representativeName'),
        law: this.$t('dispute.validation.law'),
        dispute: this.$t('dispute.validation.dispute'),
        reason: this.$t('dispute.validation.reason'),
        paidDate: this.$t('dispute.validation.paidDate'),
        paidAmount: this.$t('dispute.validation.paidAmount'),
        paidNotifyDate: this.$t('dispute.validation.paidNotifyDate'),
        expiredDate: this.$t('dispute.validation.expiredDate'),
        expiredReason: this.$t('dispute.validation.expiredReason'),
        wrongDataDetails: this.$t('dispute.validation.wrongDataDetails'),
        correctDataDetails: this.$t('dispute.validation.correctDataDetails'),
        substantiveDisputeNotice: this.$t('dispute.validation.substantiveDisputeNotice'),
        substantiveDisputeDetails: this.$t('dispute.validation.substantiveDisputeDetails'),
        notNotifiedDetails: this.$t('dispute.validation.notNotifiedDetails'),
        notNotifiedContactChange: this.$t('dispute.validation.notNotifiedContactChange'),
        reliefStatusDetails: this.$t('dispute.validation.reliefStatusDetails'),
        rightsViolationDetails: this.$t('dispute.validation.rightsViolationDetails'),
        rightsViolationContacted: this.$t('dispute.validation.rightsViolationContacted'),
        otherReasonDetails: this.$t('dispute.validation.otherReasonDetails'),
        desiredOutcome: this.$t('dispute.validation.desiredOutcome'),
        desiredOutcomeOther: this.$t('dispute.validation.desiredOutcomeOther'),
      };
    },
    uploadFiles(files, fileType) {
      if (!files.length) files = null;
      fileType === 'authorisationFile'
          ? this.details.authorisationFile = files ? files[0] : null
          : this.details.additionalFiles = files;
      if (fileType === 'authorisationFile') {
        this.showAuthFileError = false;
        if (this.$refs.authorisationFileField) {
          this.$refs.authorisationFileField.updateValidityLater();
        }
      } else {
        this.showAdditionalFileError = false;
        if (this.$refs.reasonFiles) {
          this.$refs.reasonFiles.updateValidityLater();
        }
      }
    },
    checkFieldLength() {
      const maxLength = this.isPrivate ? 11 : 8;
      if (this.details.subjectCode.length > maxLength) {
        this.details.subjectCode = this.details.subjectCode.slice(0, maxLength);
      }
    },
    reset() {
      Object.assign(this.$data, this.$options.data());
      this.syncWithProfile();
      this.populateValidationMessages();
      this.clearFeedback();
      this.showAdditionalFileError = false;
      this.showAuthFileError = false;
      this.selectedDispute = '';
      this.details.isPrivate = this.isPrivate;
      this.$refs.form.reset();
      if (this.$refs.additionalFiles) this.$refs.additionalFiles.reset();
      if (!this.isPrivate) this.$refs.authorisationFile.reset();
    },
    resetReasonFields() {
      this.details.paidDate = '';
      this.details.paidAmount = '';
      this.details.paidNotifyDate = '';
      this.details.expiredDate = '';
      this.details.expiredReason = '';
      this.details.wrongDataDetails = '';
      this.details.correctDataDetails = '';
      this.details.substantiveDisputeNotice = '';
      this.details.substantiveDisputeDetails = '';
      this.details.notNotifiedDetails = '';
      this.details.notNotifiedContactChange = '';
      this.details.bankruptcyStartDate = '';
      this.details.bankruptcyEndDate = '';
      this.details.reliefStatusDetails = '';
      this.details.reliefStartDate = '';
      this.details.reliefEndDate = '';
      this.details.rightsViolationDetails = '';
      this.details.rightsViolationContacted = '';
      this.details.otherReasonDetails = '';
      this.details.additionalFiles = null;
      this.showAdditionalFileError = false;
      if (this.$refs.additionalFiles) {
        this.$refs.additionalFiles.reset();
      }
    },
    isLawInputFilled() {
      let isChecked = this.details.isRepresentativeByLaw;
      let hasFile = this.details.authorisationFile;
      this.isLawFilled = this.isPrivate || !!(isChecked || hasFile);
    },
    showFeedback(type, feedbackType) {
      this.showMessage({
        target: 'feedback',
        type,
        text: this.$t(feedbackType),
      });
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    clearFeedback() {
      this.clearMessages('feedback');
    },
    showErrors(errors) {
      Object.entries(errors)
          .forEach((entry) => {
            const [key, value] = entry;
            this.validationMessages[key] = value[0];
            if (key.includes('additionalFiles')) {
              this.showAdditionalFileError = true;
            } else if (key.includes('authorisationFile')) {
              this.showAuthFileError = true;
            } else if (this.$refs[key]) {
              this.$refs[key].setValidity();
            }
          });
    },
    getFormData() {
      const params = { ...this.details };

      const formData = new FormData();
      Object.entries(params)
          .forEach((entry) => {
            const [key, value] = entry;
            if (value === null || value === '') return;
            if (this.dataGroups.both.includes(key)) {
              switch (key) {
                case 'additionalFiles':
                  for (const i of Object.keys(value)) {
                    formData.append(`${key}[]`, value[i], value[i].name);
                  }
                  break;
                case 'isPrivate':
                  formData.append(key, value.toString());
                  break;
                default:
                  formData.append(key, value);
              }
            } else if (this.dataGroups.private.includes(key) && this.isPrivate) {
              formData.append(key, value);
            } else if (this.dataGroups.business.includes(key) && !this.isPrivate) {
              key === 'authorisationFile' ? formData.append(key, value, value.name)
                  : formData.append(key, value);
            }
          });
      formData.append('_method', 'PUT');
      return formData;
    },
    validateDisputeInput() {
      if (this.isPrivate && !this.details.dispute) {
        this.$refs.dispute.setValidity();
        return false;
      }
      return true;
    },
    validateReasonFiles() {
      this.showAdditionalFileError = false;
      if (!this.isReasonFileRequired) {
        return { valid: true };
      }
      if (this.reasonFilesCount > 0) {
        return { valid: true };
      }
      this.showAdditionalFileError = true;
      if (this.$refs.reasonFiles) {
        this.$refs.reasonFiles.setValidity();
      }
      return {
        valid: false,
        messageKey: REQUIRED_REASON_FILE_MESSAGE_KEYS[this.details.reason] || null,
      };
    },
    send({ valid }) {
      this.isLawInputFilled();
      const reasonFilesValid = this.validateReasonFiles();
      if (!reasonFilesValid.valid) {
        this.showFeedback('danger', reasonFilesValid.messageKey || 'dispute.validation.missingRequired');
        return;
      }
      if (!valid || !this.validateDisputeInput()) {
        this.showFeedback('danger', 'dispute.validation.missingRequired');
        return;
      }
      const formData = this.getFormData();
      const config = {
        headers: {
          Authorization: this.$store.state.session.token,
          'X-Language': this.$i18n.locale,
        },
      };
      axios.post(`${this.apiUrl}/pd/dispute`, formData, config)
          .then(() => {
            this.submittedSuccessfully = true;
          })
          .catch((err) => {
            if (err.response.data.errors) { // The request was made and the server responded with a status code that falls out of the range of 2xx
              this.showErrors(err.response.data.errors);
            } else if (err.request) { // The request was made but no response was received
              this.showFeedback('danger', 'dispute.noRespError');
            } else { // Something happened in setting up the request that triggered an Error
              this.showFeedback('danger', 'dispute.generalErr');
            }
          });
    },
  },
};
</script>
<style>
.dropdown-has-error {
  border-color: #c40f11 !important;
  padding-right: calc(1.5em + 1.125rem) !important;
  background-repeat: no-repeat;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-position: right calc(0.375em + 0.28125rem) center;
  background-size: calc(0.75em + 0.5625rem) calc(0.75em + 0.5625rem);
}

.is-valid.simple-typeahead-input {
  border-color: #4caf50 !important;
  padding-right: calc(1.5em + 1.125rem) !important;
  background-repeat: no-repeat;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
  background-position: right calc(0.375em + 0.28125rem) center;
  background-size: calc(0.75em + 0.5625rem) calc(0.75em + 0.5625rem);
}
</style>
<style scoped>
input[type=checkbox] {
  display: none;
}

@media screen and (min-width: 575px) {
  input[type=checkbox] + label {
    margin-left: -0.9375rem;
  }
}

input[type=checkbox] + label {
  width: 1.875rem;
  height: 1.875rem;
  background-color: #ffffff;
  border: solid 0.0625rem #e6e6e6;
}

input[type=checkbox]:checked + label {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%234a5' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26 2.974 7.25 8 2.193z'/%3E%3C/svg%3E");
}

.label-replacement {
  font-weight: 700;
  color: #666666;
}

.label-group .label-heading {
  font-size: 1.02rem;
}

.structured-footer {
  background-color: #f0f0f0;
  padding: 1rem 0.9375rem;
  margin: 0 -0.9375rem;
}

textarea.form-control {
  height: 4rem;
}

.invalid-feedback-dispute {
  margin: 0 !important;
}

.col-form-label-details {
  padding-right: 15px;
  padding-left: 15px;
  font-weight: normal;
}
</style>
